"use client";
import { Icon, ospThemes, VirtualList } from "@ospweb/sdk-ui";
import log from "logbeacon";
import { cn } from "utils";
import { useShellRouter } from "@/src/hooks";
import { EmptyState } from "../Empty";
import { ThemeWithMarketData } from "@/src/componentsv1/themes/type";
import { ThemeSearch } from "@/src/componentsv1/themes/ThemeSearch";
import { Locale } from "@/src/utils";
import { ThemeMarketType } from "@/src/const";

interface AIInsightThemesProps {
  className?: string;
  category: string;
  locale: Locale;
  selectedTheme: ThemeWithMarketData;
  setSelectedTheme: (theme: ThemeWithMarketData) => void;
  deviceType?: "mobile" | "desktop";
  closeComponent?: React.ReactNode;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  dynamicThemesList: ThemeWithMarketData[];
}

export const AIInsightThemes = (props: AIInsightThemesProps) => {
  const {
    className,
    selectedTheme,
    setSelectedTheme,
    locale,
    deviceType: serverDeviceType = "desktop",
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    category,
    dynamicThemesList,
  } = props;
  const router = useShellRouter();
  const deviceType = serverDeviceType === "desktop" ? "desktop" : "mobile";

  // 加载骨架屏数量
  const SKELETON_COUNT = 10;
  const skeletonItems = Array.from({ length: SKELETON_COUNT }, (_, index) => ({
    id: `skeleton-${index}`,
  }));

  const handleSelectTheme = (theme: any) => {
    if (deviceType === "mobile") {
      setSelectedTheme(theme);
    } else {
      router.replace(`/news/${category}?theme=${theme?.slug}`);
    }
  };

  return (
    <div
      className={`relative h-full ${deviceType === "mobile" ? "w-full" : "w-fit"} ${className}`}
    >
      <ThemeSearch
        locale={locale}
        market={category as ThemeMarketType}
        className={cn(deviceType === "mobile" ? "w-full" : "w-[226px]")}
        onClickItem={(item) => handleSelectTheme(item as any)}
      />
      {/* 主题列表容器 */}
      <VirtualList
        className={cn(
          `flex flex-col gap-3 body_medium_m text-content_secondary mt-4 cursor-pointer overflow-y-auto no-scrollbar`,
          deviceType === "mobile" && "h-[572px]",
          category === "crypto"
            ? "h-full max-h-[calc(100vh-232px)]"
            : "h-full max-h-[calc(100vh-160px)]",
          className,
        )}
        hasNextPage={hasNextPage}
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        onEndReachedThreshold="200"
        renderLoading={
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }, (_, index) => (
              <div key={`loading-${index}`} className="flex flex-row gap-1">
                <div className="skeleton-item bg-background_white_overlay5  animate-pulse rounded-[4px] h-4 w-full" />
              </div>
            ))}
          </div>
        }
        renderNoMore={null}
      >
        {isLoading ? (
          // 骨架屏加载状态
          <div className="flex flex-col gap-3">
            {skeletonItems.map((_, index) => (
              <div key={`skeleton-${index}`} className="flex flex-row gap-2">
                <div className="skeleton-item bg-background_white_overlay5 animate-pulse rounded-[4px] h-3 w-full" />
              </div>
            ))}
          </div>
        ) : dynamicThemesList.length > 0 ? (
          <>
            {dynamicThemesList?.map((theme) => {
              const isSelected = selectedTheme?.id === theme.id;
              return (
                <div
                  data-openreplay-label="AIInsight · AIInsightThemes · div"
                  key={theme.id}
                  className={cn(
                    "body_medium_m text-content_secondary cursor-pointer",
                    isSelected && deviceType !== "mobile"
                      ? "text-content_brand_primary"
                      : "hover:text-content_primary",
                  )}
                  onClick={() => {
                    log.debug(
                      `[AIInsightThemes][trace] onClick: ${theme.theme}`,
                    );
                    setSelectedTheme?.(theme);
                  }}
                >
                  <div className="flex flex-row justify-between items-center gap-2">
                    {theme.theme}
                    {isSelected && deviceType === "mobile" && (
                      <Icon
                        name="check"
                        size={20}
                        color={ospThemes.colors.content_brand_primary}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <EmptyState
            title="No themes available"
            titleClassName="text-content_quaternary body_medium_m"
            hideImage
            hideDescription
            className="h-[200px]"
          />
        )}
      </VirtualList>
    </div>
  );
};
